import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {NetworkTypesEnum} from 'domain/usecases';
import {FeaturesEnum, OperationStateTypesEnum, QueryParamsEnum} from 'presentation/constants';

import {useFilteredNetworkList} from 'presentation/modules/booking/shared/hooks/useFilteredNetworkList';
import {useUnitsFetchStateStore} from 'presentation/modules/booking/shared/store/useUnitsFetchStateStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {useAccountInformation, useQueryParams} from 'presentation/pages';
import {useEffect, useRef} from 'react';
import {EMPTY_UNITS} from './constants';

import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {Unit} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {useHostName} from 'presentation/hooks';
import {MIN_DISTANCE_IN_KM_EXAM} from 'presentation/modules/booking/exam/ui/components/ExamSearchBar/hooks/useExamSearchBar/constants';
import {
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useGetExamUnitsMutation} from '../../mutations/useGetExamUnitsMutation';
import {useGetExamAccreditedUnitsMutation} from '../../mutations/useGetExamAccreditedUnitsMutation';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useReverseNetworkOrder} from 'presentation/hooks/useReverseNetworkOrder';

export function useExamUnits() {
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const {isPPOPlan} = useAccountInformation();
	const {
		setSearchParams,
		SEARCH_PARAMS: {localityDistance, query, distanceOrigin},
	} = useQueryParams();
	const {isManualLocation} = useBookingStore(state => state.bookingData);
	const {isEnabled: isExamsAccreditedNetworkEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_EXAMS_ACCREDITED_NETWORK,
	);
	const {isEnabled: isPPOAdvanceUnitsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_PPO_ADVANCE_UNITS,
	);
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);

	const setUnitResponseState = useUnitsListStore(state => state.setUnitsList);
	const unitResponseState = useUnitsListStore(state => state.unitsList) as Unit<FlowEnum.EXAM>;
	const setOperationState = useUnitsFetchStateStore(state => state.setUnitListFetchState);
	const operationState = useUnitsFetchStateStore(state => state.unitListFetchState);
	const filteredNetworkList = useFilteredNetworkList(query) as Unit<FlowEnum.EXAM>;
	const abortControllerRef = useRef<AbortController | null>(null);

	const isManualDistanceSearch = distanceOrigin === MANUAL_DISTANCE_ORIGIN;
	const distanceFilterAtMaxDistance = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS;

	const {
		mutateAsync: fetchUnits,
		isSuccess,
		data: units,
		error,
		isError,
	} = useGetExamUnitsMutation();
	const {mutateAsync: fetchAccreditedUnits} = useGetExamAccreditedUnitsMutation();

	const notFoundUnitsError =
		error instanceof APIError && error.statusCode === HttpStatusCode.unprocessable;

	const isLoading = operationState === OperationStateTypesEnum.SUBMITTING;

	const filteredNetworkListWithoutAdvanceUnits = {
		...filteredNetworkList,
		units: {
			unitsByLocality: filteredNetworkList.units.unitsByLocality.filter(
				unit => !unit.isAdvanceUnit,
			),
			unitsByDistance: filteredNetworkList.units.unitsByDistance.filter(
				unit => !unit.isAdvanceUnit,
			),
			unitsSupportAccredited: filteredNetworkList.units.unitsSupportAccredited.filter(
				unit => !unit.isAdvanceUnit,
			),
		},
	};

	const advanceUnits = filteredNetworkList.units.unitsByLocality
		.filter(unit => unit.isAdvanceUnit)
		.concat(filteredNetworkList.units.unitsByDistance.filter(unit => unit.isAdvanceUnit));

	const showAdvanceUnits = isPPOAdvanceUnitsEnabled && isPPOPlan && advanceUnits.length > 0;

	const showsAccreditedNetworkByProduct = isGndiSaoPaulo || isNotredameSaude;
	const hideAdvanceUnitsDivider =
		!!query &&
		filteredNetworkListWithoutAdvanceUnits.units.unitsByLocality?.length === 0 &&
		filteredNetworkListWithoutAdvanceUnits.units.unitsByDistance?.length === 0 &&
		filteredNetworkListWithoutAdvanceUnits.units.unitsSupportAccredited?.length === 0;

	const filteredListIsEmpty =
		!filteredNetworkList ||
		(filteredNetworkList.units.unitsByLocality?.length === 0 &&
			filteredNetworkList.units.unitsByDistance?.length === 0 &&
			filteredNetworkList.units.unitsSupportAccredited?.length === 0);

	const hasAnyUnit = Boolean(
		unitResponseState?.units?.unitsByLocality?.length ||
		unitResponseState?.units?.unitsByDistance?.length ||
		unitResponseState?.units?.unitsSupportAccredited?.length,
	);

	const showNotFoundState = isUnitsNetworksEnabled
		? notFoundUnitsError || (Boolean(query) && filteredListIsEmpty && hasAnyUnit)
		: filteredListIsEmpty || notFoundUnitsError;

	const showResultFoundTitle = query;

	const unitsList =
		isPPOAdvanceUnitsEnabled && isPPOPlan
			? filteredNetworkListWithoutAdvanceUnits
			: filteredNetworkList;

	const {shouldReverseNetworkOrder} = useReverseNetworkOrder({flowType: 'EXAM'});

	const setDefaultDistanceUrl = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, MIN_DISTANCE_IN_KM_EXAM);
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);

			return prev;
		});
	};

	const setMaxDistanceSearchParams = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, MAX_DISTANCE_FILTER_UNITS.toString());
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);

			return prev;
		});
	};

	const handleFetchUnits = async () => {
		setOperationState(OperationStateTypesEnum.SUBMITTING);

		if (!isManualLocation && !localityDistance) {
			setDefaultDistanceUrl();
			return;
		}

		abortControllerRef.current = new AbortController();

		try {
			const units = await fetchUnits({signal: abortControllerRef.current.signal});
			const localityListEmpty = units?.unidadesPorLocal?.length === 0;
			const distanceListEmpty = units?.unidadesPorRaio?.length === 0;

			if (!localityDistance && localityListEmpty) {
				setDefaultDistanceUrl();
				return;
			}

			const hasNoSelfNetworkUnits = localityListEmpty && distanceListEmpty;

			const shouldExpandToMaxDistance =
				isUnitsNetworksEnabled &&
				!isManualDistanceSearch &&
				!distanceFilterAtMaxDistance &&
				hasNoSelfNetworkUnits;

			if (shouldExpandToMaxDistance) {
				setMaxDistanceSearchParams();
				return;
			}

			let accreditedUnits;

			const shouldFetchAccreditedUnits =
				isExamsAccreditedNetworkEnabled &&
				(showsAccreditedNetworkByProduct || (localityListEmpty && distanceListEmpty));

			if (shouldFetchAccreditedUnits) {
				try {
					accreditedUnits = await fetchAccreditedUnits({
						signal: abortControllerRef.current.signal,
					});
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error(error);
				}
			}

			const accreditedUnitsEmpty =
				!accreditedUnits || accreditedUnits?.redeCredenciada?.length === 0;

			const emptyUnits =
				(!units && !accreditedUnits) ||
				(localityListEmpty && distanceListEmpty && accreditedUnitsEmpty);

			if (emptyUnits) {
				setUnitResponseState(EMPTY_UNITS);
				setOperationState(OperationStateTypesEnum.DONE);
				return;
			}

			setUnitResponseState({
				networkType: {
					code: '',
					name: NetworkTypesEnum.SELF,
				},
				units: {
					unitsByLocality: units?.unidadesPorLocal || [],
					unitsByDistance: units?.unidadesPorRaio || [],
					unitsSupportAccredited: accreditedUnits?.redeCredenciada || [],
				},
			});
			setOperationState(OperationStateTypesEnum.DONE);
		} catch {
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const showAccreditedNetwork = () => {
		if (!isExamsAccreditedNetworkEnabled) return false;
		if (showsAccreditedNetworkByProduct) return true;

		const localityListEmpty = units?.unidadesPorLocal?.length === 0;
		const distanceListEmpty = units?.unidadesPorRaio?.length === 0;

		return isSuccess && localityListEmpty && distanceListEmpty;
	};

	const showSelfNetwork = () => {
		if (isUnitsNetworksEnabled || isGndiSaoPaulo) return true;

		return !showAccreditedNetwork();
	};

	useEffect(() => {
		handleFetchUnits();
	}, [localityDistance]);

	useEffect(
		() => () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		},
		[],
	);

	return {
		unitsList,
		refetch: handleFetchUnits,
		showNotFoundState,
		isLoading,
		isError: isError && !notFoundUnitsError,
		showResultFoundTitle,
		showAccreditedNetwork,
		showSelfNetwork,
		shouldReverseNetworkOrder,
		showAdvanceUnits,
		advanceUnits,
		hideAdvanceUnitsDivider,
	};
}
